import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import "../../css/AddTeachers.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function AddTeachers() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    gender: '',
    address: '',
    qualification: '',
    experience: '',
    specialization: '',
    joiningDate: '',
    employeeId: '',
    department: '',
    position: '',
    salary: '',
    bloodGroup: '',
  });

  const [activeTab, setActiveTab] = useState('individual');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedTeachers, setUploadedTeachers] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please login again.");
      return;
    }
    setIsUploading(true);
    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/teachers/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Teacher Added Successfully");
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dob: '',
          gender: '',
          address: '',
          qualification: '',
          experience: '',
          specialization: '',
          joiningDate: '',
          employeeId: '',
          department: '',
          position: '',
          salary: '',
          bloodGroup: '',
        });
      } else {
        toast.error(data.message || "Failed to add teacher");
      }
    } catch (error) {
      console.error("Error submitting teacher data:", error);
      toast.error("An error occurred while submitting the data");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadedTeachers([]);
    setUploadSuccess(false);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file first!');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const headers = jsonData[0];
        const headerMapping = {
          'first name': 'firstName',
          'last name': 'lastName',
          'firstname': 'firstName',
          'lastname': 'lastName',
          'first': 'firstName',
          'last': 'lastName',
          'date of birth': 'dob',
          'dateofbirth': 'dob',
          'date': 'dob',
          'gender': 'gender',
          'email': 'email',
          'phone': 'phone',
          'address': 'address',
          'qualification': 'qualification',
          'experience': 'experience',
          'specialization': 'specialization',
          'department': 'department',
          'position': 'position',
          'employee id': 'employeeId',
          'employeeid': 'employeeId',
          'id': 'employeeId',
          'joining date': 'joiningDate',
          'joiningdate': 'joiningDate',
          'salary': 'salary',
          'blood group': 'bloodGroup',
          'bloodgroup': 'bloodGroup',
          'blood': 'bloodGroup'
        };

        const teachers = [];
        const errors = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          // Skip empty rows
          if (row.length === 0 || row.every(cell => cell === "")) continue;

          const teacher = {};
          let hasError = false;

          headers.forEach((header, index) => {
            if (!header) return;

            const cleanHeader = header
              .replace(/\*/g, '')
              .replace(/\([^)]*\)/g, '')
              .trim()
              .toLowerCase();

            const schemaProperty = headerMapping[cleanHeader] || cleanHeader;
            if (!headerMapping[cleanHeader] && !row[index]) return;

            teacher[schemaProperty] = row[index] || '';
          });
          
          if (teacher.dob) {
            teacher.dob = new Date(teacher.dob).toISOString().split('T')[0];
            if (isNaN(new Date(teacher.dob).getTime())) {
              errors.push(`Row ${i + 1}: Invalid date of birth format. Expected YYYY-MM-DD`);
              hasError = true;
            }
          } else {
            errors.push(`Row ${i + 1}: Missing required field: Date of Birth`);
            hasError = true;
          }

          if (teacher.joiningDate) {
            teacher.joiningDate = new Date(teacher.joiningDate).toISOString().split('T')[0];
            if (isNaN(new Date(teacher.joiningDate).getTime())) {
              errors.push(`Row ${i + 1}: Invalid joining date format. Expected YYYY-MM-DD`);
              hasError = true;
            }
          } else {
            errors.push(`Row ${i + 1}: Missing required field: Joining Date`);
            hasError = true;
          }
          
          if (teacher.experience) {
            teacher.experience = parseInt(teacher.experience.toString().replace(/\D/g, ''), 10);
            if (isNaN(teacher.experience)) {
              errors.push(`Row ${i + 1}: Experience must be a number`);
              hasError = true;
            }
          } else {
            errors.push(`Row ${i + 1}: Missing required field: Experience`);
            hasError = true;
          }

          if (teacher.salary) {
            teacher.salary = parseFloat(teacher.salary.toString().replace(/[^\d.]/g, ''));
            if (isNaN(teacher.salary)) {
              errors.push(`Row ${i + 1}: Salary must be a number`);
              hasError = true;
            }
          } else {
            errors.push(`Row ${i + 1}: Missing required field: Salary`);
            hasError = true;
          }

          const requiredFields = [
            { field: 'firstName', label: 'First Name' },
            { field: 'lastName', label: 'Last Name' },
            { field: 'email', label: 'Email' },
            { field: 'phone', label: 'Phone' },
            { field: 'gender', label: 'Gender' },
            { field: 'address', label: 'Address' },
            { field: 'qualification', label: 'Qualification' },
            { field: 'specialization', label: 'Specialization' },
            { field: 'department', label: 'Department' },
            { field: 'position', label: 'Position' },
            { field: 'employeeId', label: 'Employee ID' }
          ];

          requiredFields.forEach(({ field, label }) => {
            if (!teacher[field]) {
              errors.push(`Row ${i + 1}: Missing required field: ${label}`);
              hasError = true;
            }
          });

          if (teacher.gender) {
            teacher.gender = teacher.gender.toLowerCase();
            if (!['male', 'female', 'other'].includes(teacher.gender)) {
              errors.push(`Row ${i + 1}: Invalid gender value. Must be Male, Female, or Other`);
              hasError = true;
            }
          }

          const validBloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
          if (teacher.bloodGroup && !validBloodGroups.includes(teacher.bloodGroup)) {
            errors.push(`Row ${i + 1}: Invalid blood group. Must be one of ${validBloodGroups.join(', ')}`);
            hasError = true;
          }

          if (!hasError) {
            teachers.push(teacher);
          }
        }

        if (errors.length > 0) {
          const errorMessage = `Found ${errors.length} errors in the data:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more errors` : ''}`;
          console.error('Validation errors prevented upload:', errorMessage);
          toast.error(errorMessage);
          setIsUploading(false);
          return;
        }

        if (teachers.length === 0) {
          console.error('No valid teachers found after validation');
          toast.error('No valid teacher data found in the file.');
          setIsUploading(false);
          return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          setIsUploading(false);
          return;
        }

        try {
          const response = await fetch("https://schoolpro-api.uniums.com/api/teachers/bulk", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(teachers),
          });

          const result = await response.json();
          if (response.ok) {
            setUploadedTeachers(teachers);
            setUploadSuccess(true);
            toast.success(`Successfully added ${teachers.length} teachers to the database.`);
          } else {
            toast.error(result.message || "Failed to add teachers");
            setUploadSuccess(false);
          }
        } catch (error) {
          console.error('Error uploading teachers to backend:', error);
          toast.error(`Error uploading teachers: ${error.message}`);
          setUploadSuccess(false);
        }

      } catch (error) {
        console.error('Error processing file:', error);
        toast.error(`Error processing file: ${error.message}`);
        setUploadSuccess(false);
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      toast.error('Error reading file');
      setIsUploading(false);
      setUploadSuccess(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    try {
      const headers = [
        "First Name*",
        "Last Name*",
        "Date of Birth* (YYYY-MM-DD)",
        "Gender* (Male/Female/Other)",
        "Email*",
        "Phone*",
        "Address*",
        "Qualification* (PhD/Masters/Bachelors)",
        "Experience* (Years)",
        "Specialization*",
        "Department* (Science/Math/English/etc)",
        "Position* (Teacher/HOD/Principal)",
        "Employee ID*",
        "Joining Date* (YYYY-MM-DD)",
        "Salary*",
        "Blood Group"
      ];

      const sampleRow = [
        "John",
        "Smith",
        "1985-05-15",
        "Male",
        "john.smith@school.com",
        "1234567890",
        "123 Faculty Avenue, City",
        "Masters",
        "8",
        "Mathematics",
        "Mathematics",
        "Senior Teacher",
        "EMP12345",
        "2018-06-15",
        "45000",
        "O+"
      ];

      const sampleRow2 = [
        "Mary",
        "Johnson",
        "1990-08-22",
        "Female",
        "mary.johnson@school.com",
        "2345678901",
        "456 Education Road, City",
        "PhD",
        "12",
        "Physics",
        "Science",
        "HOD",
        "EMP12346",
        "2015-06-16",
        "65000",
        "A+"
      ];

      const ws = XLSX.utils.aoa_to_sheet([
        headers,
        sampleRow,
        sampleRow2,
        Array(headers.length).fill("")
      ]);

      const colWidths = headers.map(h => ({ wch: Math.max(h.length, 15) }));
      ws['!cols'] = colWidths;

      ws.A5 = {
        v: "INSTRUCTIONS: Fill in the teacher details starting from row 4. Fields marked with * are required. Please maintain the date format as YYYY-MM-DD.",
        t: 's'
      };
      ws.A6 = {
        v: "You can delete the sample rows (2-3) before importing the file.",
        t: 's'
      };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Teacher Template");
      XLSX.writeFile(wb, "Teacher_Data_Template.xlsx");
    } catch (error) {
      console.error("Error generating Excel template:", error);
      toast.error("Failed to generate Excel template. Please try again.");
    }
  };

  return (
    <>
      <div className="addteacher-container">
        <h1 className="addteacher-title">Add Teachers</h1>
        {isUploading && <Loader />}
        <div className="addteacher-tabs">
          <button
            className={`addteacher-tab ${activeTab === 'individual' ? 'addteacher-tab-active' : ''}`}
            onClick={() => setActiveTab('individual')}
          >
            Add Individual Teacher
          </button>
          <button
            className={`addteacher-tab ${activeTab === 'bulk' ? 'addteacher-tab-active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            Bulk Upload
          </button>
        </div>

        {activeTab === 'individual' ? (
          <div className="addteacher-form-container">
            <form onSubmit={handleSubmit} className="addteacher-form">
              <div className="addteacher-form-section">
                <h2 className="addteacher-section-title">Personal Information</h2>
                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">First Name*</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Last Name*</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                </div>

                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Date of Birth*</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Gender*</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="addteacher-select"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Email*</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Phone*</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                </div>

                <div className="addteacher-form-group">
                  <label className="addteacher-label">Address*</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="addteacher-textarea"
                    required
                  ></textarea>
                </div>

                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Blood Group</label>
                    <select
                      name="bloodGroup"
                      value={formData.bloodGroup}
                      onChange={handleInputChange}
                      className="addteacher-select"
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="addteacher-form-section">
                <h2 className="addteacher-section-title">Professional Information</h2>
                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Qualification*</label>
                    <select
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleInputChange}
                      className="addteacher-select"
                      required
                    >
                      <option value="">Select Qualification</option>
                      <option value="phd">PhD</option>
                      <option value="masters">Masters</option>
                      <option value="bachelors">Bachelors</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Experience (Years)*</label>
                    <input
                      type="number"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Specialization*</label>
                    <input
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Department*</label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      className="addteacher-select"
                      required
                    >
                      <option value="">Select Department</option>
                      <option value="mathematics">Mathematics</option>
                      <option value="science">Science</option>
                      <option value="english">English</option>
                      <option value="social">Social Studies</option>
                      <option value="languages">Languages</option>
                      <option value="physical">Physical Education</option>
                      <option value="arts">Arts & Crafts</option>
                      <option value="computer">Computer Science</option>
                      <option value="admin">Administration</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="addteacher-form-section">
                <h2 className="addteacher-section-title">Employment Information</h2>
                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Employee ID*</label>
                    <input
                      type="text"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Position*</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="addteacher-select"
                      required
                    >
                      <option value="">Select Position</option>
                      <option value="teacher">Teacher</option>
                      <option value="seniorTeacher">Senior Teacher</option>
                      <option value="hod">Head of Department</option>
                      <option value="vicePrincipal">Vice Principal</option>
                      <option value="principal">Principal</option>
                      <option value="staff">Administrative Staff</option>
                    </select>
                  </div>
                </div>

                <div className="addteacher-form-row">
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Joining Date*</label>
                    <input
                      type="date"
                      name="joiningDate"
                      value={formData.joiningDate}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      required
                    />
                  </div>
                  <div className="addteacher-form-group">
                    <label className="addteacher-label">Salary*</label>
                    <input
                      type="number"
                      name="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                      className="addteacher-input"
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="addteacher-button-container">
                <button type="submit" className="addteacher-submit-btn">Add Teacher</button>
                <button type="reset" className="addteacher-reset-btn">Reset Form</button>
              </div>
            </form>
          </div>
        ) : (
          <div className="addteacher-bulk-container">
            <div className="addteacher-bulk-instructions">
              <h2 className="addteacher-section-title">Bulk Upload Instructions</h2>
              <ol className="addteacher-instruction-list">
                <li>Download the Excel template using the button below</li>
                <li>Fill in the required teacher information in the template</li>
                <li>Save the file and upload it using the form below</li>
                <li>Review any errors that may occur during upload</li>
              </ol>
              <button
                className="addteacher-download-btn"
                onClick={downloadTemplate}
              >
                Download Excel Template
              </button>
            </div>

            <div className="addteacher-upload-container">
              <h2 className="addteacher-section-title">Upload Excel File</h2>
              <form onSubmit={handleFileUpload} className="addteacher-upload-form">
                <div className="addteacher-file-input-container">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileChange}
                    className="addteacher-file-input"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="addteacher-file-label">
                    {file ? file.name : 'Choose File'}
                  </label>
                </div>

                <button
                  type="submit"
                  className="addteacher-upload-btn"
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload File'}
                </button>
              </form>

              {uploadSuccess && (
                <div className="addteacher-success-message">
                  File uploaded successfully! {uploadedTeachers.length} teachers have been added to the system.
                </div>
              )}

              {uploadSuccess && uploadedTeachers.length > 0 && (
                <div className="addteacher-upload-summary">
                  <h3 className="addteacher-summary-title">Upload Summary</h3>
                  <div className="addteacher-summary-content">
                    <p><strong>Total Teachers:</strong> {uploadedTeachers.length}</p>
                    <p><strong>Processed:</strong> {uploadedTeachers.length}</p>
                    <p><strong>Status:</strong> Ready to be added to database</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
}

export default AddTeachers;